/**
 * The Disputes tab's table (docs/features/v1-completion/L_credit_control_spec.md §3.3, §4.6; mock-up
 * L_credit_control.html state 4): every dispute finance has recorded, open or resolved, newest first. Resolve shows
 * only on an open dispute whose response carries `resolve_dispute_url` (credit_control:manage). A "Paid" tag marks an
 * open dispute whose invoice was paid in Xero meanwhile: it left aged debt but still wants closing.
 */
import { Link } from "react-router";
import type { DisputeRow } from "@shared-core/data/aged-debt-types";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";

export function DisputesTable({
  rows,
  onResolve,
}: {
  rows: DisputeRow[];
  onResolve: (row: DisputeRow) => void;
}) {
  return (
    <Table className="[&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6">
      <TableHeader>
        <TableRow>
          <TableHead>Invoice</TableHead>
          <TableHead>Payer</TableHead>
          <TableHead className="text-right">Invoice total</TableHead>
          <TableHead>Raised</TableHead>
          <TableHead>What the customer says</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.dispute_id} className="align-top">
            <TableCell className="font-medium">
              <Link
                to={paths.invoiceDetail({ invoiceNo: row.invoice_no })}
                className="hover:underline"
              >
                {row.invoice_no}
              </Link>
              {row.order_no && (
                <div className="text-xs font-normal text-muted-foreground">
                  {row.order_no}
                </div>
              )}
            </TableCell>
            <TableCell>
              {row.payer_name}
              {row.customer_name && row.customer_name !== row.payer_name && (
                <div className="text-xs text-muted-foreground">
                  {row.customer_name}
                </div>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {row.gross_pence === null
                ? "–"
                : formatQuotedPrice(row.gross_pence)}
            </TableCell>
            <TableCell className="whitespace-nowrap tabular-nums">
              {formatShortDateOrDash(row.raised_at)}
              {row.raised_by_name && (
                <div className="text-xs text-muted-foreground">
                  {row.raised_by_name}
                </div>
              )}
            </TableCell>
            <TableCell className="max-w-md whitespace-normal">
              {row.reason}
              {row.status === "resolved" && (
                <div className="text-xs text-muted-foreground">
                  Resolved {formatShortDateOrDash(row.resolved_at)}
                  {row.resolved_by_name ? ` by ${row.resolved_by_name}` : ""}
                  {row.resolution_note ? `: ${row.resolution_note}` : ""}
                </div>
              )}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              <Badge
                variant={row.status === "open" ? "warning" : "neutral"}
                className="font-normal"
              >
                {row.status_label}
              </Badge>{" "}
              {row.status === "open" && row.invoice_settled && (
                <Badge variant="success" className="font-normal">
                  Paid
                </Badge>
              )}
            </TableCell>
            <TableCell>
              {row.resolve_dispute_url && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-label={`Resolve dispute on ${row.invoice_no}`}
                  onClick={() => onResolve(row)}
                >
                  Resolve…
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
